import type React from 'react';
import { useState } from 'react';
import { NavLink, useSearchParams } from 'react-router';

import { Button, TextField } from 'ui';
import { useChangeUnverifiedEmail, useResendEmail } from 'hooks/useEmailVerification';
import { checkOtpPhone, useStartOtp } from 'hooks/useOtpChallenge';
import { parseEmailNotVerifiedError, parsePasswordNotSetError } from 'utils/emailNotVerified';
import { completeOtpAuthSession } from 'utils/completeOtpAuthSession';
import { useNavigate } from 'react-router';
import { OtpCodeScreen } from 'modules/Otp/OtpCodeScreen';
import {
  clearOtpScreen,
  loadOtpScreen,
  otpScreenSecondsLeft,
  patchOtpScreen,
  saveOtpScreen,
} from 'utils/otpScreenStorage';
import { PhoneIcon } from 'ui/icons';

import classes from './Login.module.scss';
import { loginSchema } from './schema';
import { useLoginMutation } from './services';
import { captureReturnTo, consumeReturnTo, withReturnToQuery } from 'utils/returnTo';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { Form, Formik } from 'formik';

type PhoneMode = 'choice' | 'otp' | 'password';

export const Login: React.FC = () => {
  const loginApi = useLoginMutation();
  const startOtp = useStartOtp(true);
  const resend = useResendEmail();
  const changeEmail = useChangeUnverifiedEmail();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = captureReturnTo(searchParams);
  const [blocked, setBlocked] = useState<ReturnType<typeof parseEmailNotVerifiedError>>(null);
  const [newEmail, setNewEmail] = useState('');
  const [showChange, setShowChange] = useState(false);
  // F5 на экране кода: берем тот же challenge из sessionStorage, код заново не отправляем.
  const [restored] = useState(() => loadOtpScreen('login'));
  const [phoneMode, setPhoneMode] = useState<PhoneMode>(restored ? 'otp' : 'choice');
  const [passwordHint, setPasswordHint] = useState('');
  // Проверка номера до отправки кода: аккаунта нет / не подтвержден / отключен - код не шлем.
  const [phoneLookup, setPhoneLookup] = useState<{ message: string; register: boolean } | null>(
    null,
  );
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [otpStart, setOtpStart] = useState<{
    phone: string;
    challengeId: string;
    channel: string;
    cascadeAfter?: number;
    error?: string;
  } | null>(() =>
    restored
      ? {
          phone: restored.phone,
          challengeId: restored.challengeId,
          channel: restored.channel,
          cascadeAfter: otpScreenSecondsLeft(restored),
          error: restored.error,
        }
      : null,
  );
  const dropOtp = () => {
    clearOtpScreen();
    setOtpStart(null);
  };

  const initialValues = {
    method: (searchParams.get('email') ? 'email' : 'phone') as 'email' | 'phone',
    // ?email=1 только включает вход по почте; адрес подставляем, лишь если это email.
    email: (searchParams.get('email') || '').includes('@') ? searchParams.get('email') || '' : '',
    phone: searchParams.get('phone') || restored?.phone || '',
    password: '',
  };

  return (
    <section className={classes.wrapper}>
      <main className={classes.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={loginSchema}
          onSubmit={async (values, helpers) => {
            setBlocked(null);
            setPasswordHint('');
            if (values.method === 'phone' && phoneMode !== 'password') {
              return;
            }
            if (values.method === 'phone' && !values.password) {
              setPasswordHint('Введите пароль или войдите по коду.');
              return;
            }
            try {
              await loginApi.mutateAsync(
                values.method === 'phone'
                  ? { phone: values.phone, password: values.password }
                  : { email: values.email, password: values.password },
              );
            } catch (error) {
              const payload = parseEmailNotVerifiedError(error);
              if (payload) {
                setBlocked(payload);
                if (payload.email) {
                  localStorage.setItem('registration_email', payload.email);
                }
                return;
              }
              const status = (error as { response?: { status?: number } })?.response?.status;
              if (status === 401) {
                helpers.setFieldTouched('password', true, false);
                helpers.setFieldError(
                  'password',
                  values.method === 'email'
                    ? 'Неверная почта или пароль'
                    : 'Неверный телефон или пароль',
                );
              }
              // Бан/отключение: показываем причину прямо под формой, а не только во всплывающем тосте.
              const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
              if (status === 403 && typeof detail === 'string' && /заблокирован|отключ/i.test(detail)) {
                setPasswordHint(detail);
                return;
              }
              const notSet = parsePasswordNotSetError(error);
              if (notSet) {
                setPasswordHint(notSet.message);
                if (notSet.phone_otp_available && notSet.phone) {
                  helpers.setFieldValue('method', 'phone');
                  helpers.setFieldValue('phone', notSet.phone);
                  setPhoneMode('choice');
                }
              }
            }
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, handleBlur, setFieldValue }) => {
            const miss = (key: 'email' | 'phone' | 'password') =>
              touched[key] && errors[key] ? String(errors[key]) : undefined;
            const requestCode = async () => {
              setPasswordHint('');
              setPhoneLookup(null);
              dropOtp();
              setPhoneChecking(true);
              try {
                const check = await checkOtpPhone(values.phone, 'login');
                if (check.status !== 'ok') {
                  setPhoneLookup({
                    message:
                      check.status === 'not_found'
                        ? 'Аккаунт с этим номером не найден'
                        : check.message || 'Войти по этому номеру нельзя.',
                    register: check.status === 'not_found' || check.status === 'unverified',
                  });
                  return;
                }
              } catch (error) {
                setPhoneLookup({
                  message: getApiErrorMessage(error, 'Не удалось проверить номер. Попробуйте позже.'),
                  register: false,
                });
                return;
              } finally {
                setPhoneChecking(false);
              }
              try {
                const data = await startOtp.mutateAsync({
                  purpose: 'login',
                  phone: values.phone,
                });
                if (data.stub) {
                  setPasswordHint(
                    'Код не отправлен провайдером. Проверьте номер или попробуйте позже. Не ждите код впустую.',
                  );
                  return;
                }
                if (!data.challenge_id) {
                  setPasswordHint('Не удалось начать подтверждение. Попробуйте еще раз.');
                  return;
                }
                setOtpStart({
                  phone: values.phone,
                  challengeId: data.challenge_id,
                  channel: data.channel,
                  cascadeAfter: data.cascade_after,
                });
                saveOtpScreen({
                  flow: 'login',
                  purpose: 'login',
                  phone: values.phone,
                  challengeId: data.challenge_id,
                  channel: data.channel,
                  cascadeAfter: data.cascade_after,
                });
                setPhoneMode('otp');
              } catch (error) {
                const msg = getApiErrorMessage(
                  error,
                  'Не удалось отправить код. Если регистрация не подтверждена - завершите ее сначала.',
                );
                setPasswordHint('');
                setOtpStart({
                  phone: values.phone,
                  challengeId: '',
                  channel: 'sms',
                  cascadeAfter: 0,
                  error: msg,
                });
                saveOtpScreen({
                  flow: 'login',
                  purpose: 'login',
                  phone: values.phone,
                  challengeId: '',
                  channel: 'sms',
                  cascadeAfter: 0,
                  error: msg,
                });
                setPhoneMode('otp');
              }
            };

            // Figma 21:4837: код входа - отдельный экран вместо формы (Formik остается, номер сохраняется).
            if (otpStart && phoneMode === 'otp') {
              return (
                <OtpCodeScreen
                  purpose='login'
                  phone={otpStart.phone}
                  challengeId={otpStart.challengeId}
                  channel={otpStart.channel}
                  cascadeAfter={otpStart.cascadeAfter}
                  initialError={otpStart.error}
                  onChallengeChange={(next) => patchOtpScreen('login', next)}
                  onChangeNumber={() => {
                    dropOtp();
                    setPhoneMode('choice');
                  }}
                  onVerified={(data) => {
                    clearOtpScreen();
                    completeOtpAuthSession(data, navigate, consumeReturnTo());
                  }}
                />
              );
            }

            return (
            <Form className={classes.form}>
              <div className={classes.heading}>
                <h1 className={classes.title}>Вход в аккаунт</h1>
                <p className={classes.subtitle}>
                  {values.method === 'email'
                    ? 'Войдите с помощью почты и пароля.'
                    : 'Вернитесь к своим задачам и занятиям.'}
                </p>
              </div>

              <fieldset className={classes.methodFieldset}>
              <legend className={classes.methodLegend}>Способ входа</legend>
              <div className={classes.methodRow} role='radiogroup'>
                <label className={classes.methodItem}>
                  <input
                    type='radio'
                    name='method'
                    checked={values.method === 'phone'}
                    onChange={() => {
                      setFieldValue('method', 'phone');
                      setPhoneMode('choice');
                      dropOtp();
                      setPasswordHint('');
                    }}
                  />
                  <span>Телефон</span>
                </label>
                <label className={classes.methodItem}>
                  <input
                    type='radio'
                    name='method'
                    checked={values.method === 'email'}
                    onChange={() => {
                      setFieldValue('method', 'email');
                      setPhoneMode('choice');
                      dropOtp();
                      setPasswordHint('');
                    }}
                  />
                  <span>Почта</span>
                </label>
              </div>
              </fieldset>

              {values.method === 'email' ? (
                <TextField
                  shape='brick'
                  label='Почта'
                  placeholder='name@example.com'
                  size='lg'
                  name='email'
                  className={`fig-auth-field ${classes.input}`}
                  value={values.email}
                  onChange={(val) => setFieldValue('email', val)}
                  onBlur={handleBlur}
                  isInvalid={miss('email')}
                  disabled={loginApi.isPending}
                />
              ) : (
                <TextField
                  shape='brick'
                  label='Телефон'
                  placeholder='+7 (___) ___-__-__'
                  size='lg'
                  name='phone'
                  mask='ru-phone'
                  className={`fig-auth-field ${classes.input} ${classes.phoneField}`}
                  startAdornment={
                    <span className={classes.phoneIcon} aria-hidden>
                      <PhoneIcon />
                    </span>
                  }
                  value={values.phone}
                  onChange={(val) => {
                    setPhoneLookup(null);
                    setFieldValue('phone', val);
                  }}
                  onBlur={handleBlur}
                  isInvalid={phoneLookup?.message || miss('phone')}
                  disabled={loginApi.isPending}
                />
              )}

              {values.method === 'phone' && phoneLookup?.register ? (
                <NavLink
                  to={withReturnToQuery(
                    `/registration?phone=${encodeURIComponent(values.phone)}`,
                    returnTo,
                  )}
                  className={classes.lookupAction}
                >
                  Зарегистрироваться
                </NavLink>
              ) : null}

              {values.method === 'phone' && phoneMode === 'choice' && !phoneLookup ? (
                <p className={classes.fieldHint}>Отправим код для входа на ваш номер.</p>
              ) : null}

              {/* Figma 21:4253: в режиме пароля кнопки выбора скрыты, вместо них ссылка «Войти по коду». */}
              {values.method === 'phone' && phoneMode !== 'password' ? (
                <div className={classes.choiceRow}>
                  <Button
                    type='button'
                    variant='filled'
                    shape='brick'
                    loading={startOtp.isPending || phoneChecking}
                    // Неактивна, пока номер не введен полностью (11 цифр, как в схеме формы).
                    disabled={
                      startOtp.isPending ||
                      phoneChecking ||
                      values.phone.replace(/\D/g, '').length < 11
                    }
                    className={classes.choicePrimary}
                    onClick={() => void requestCode()}
                  >
                    Получить код
                  </Button>
                  <Button
                    type='button'
                    variant='outlined'
                    shape='brick'
                    className={classes.choiceSecondary}
                    onClick={() => {
                      setPhoneMode('password');
                      dropOtp();
                    }}
                  >
                    Ввести пароль
                  </Button>
                </div>
              ) : null}

              {values.method === 'email' || phoneMode === 'password' ? (
                <TextField
                  shape='brick'
                  label='Пароль'
                  placeholder='Пароль'
                  size='lg'
                  type='password'
                  name='password'
                  className={`fig-auth-field ${classes.input}`}
                  value={values.password}
                  onChange={(val) => setFieldValue('password', val)}
                  onBlur={handleBlur}
                  isInvalid={miss('password')}
                  disabled={loginApi.isPending}
                />
              ) : null}

              {passwordHint ? <p className='fig-field-error'>{passwordHint}</p> : null}


              {blocked ? (
                <div className={classes.unverifiedBox}>
                  <p>
                    {blocked.message}{' '}
                    {blocked.email_sent ? 'Проверьте папку "Спам".' : null}
                  </p>
                  {blocked.can_resend ? (
                    <Button
                      type='button'
                      variant='filled'
                      shape='brick'
                      color='secondary'
                      widthFullSize
                      loading={resend.isPending}
                      disabled={resend.isPending}
                      onClick={() =>
                        resend.mutate(blocked.email || values.email, {
                          onSuccess: () =>
                            setBlocked((prev) =>
                              prev ? { ...prev, email_sent: true } : prev,
                            ),
                        })
                      }
                    >
                      Отправить письмо еще раз
                    </Button>
                  ) : (
                    <NavLink to='/registration' className={classes.link}>
                      Зарегистрироваться снова
                    </NavLink>
                  )}
                  {blocked.can_change_email ? (
                    <Button
                      type='button'
                      variant='clear'
                      color='primary'
                      size='clear'
                      className={classes.link}
                      style={{ marginTop: 12 }}
                      onClick={() => setShowChange((v) => !v)}
                    >
                      Не тот адрес? Исправить почту
                    </Button>
                  ) : null}
                  {showChange && blocked.can_change_email ? (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <TextField
                        shape='brick'
                        label='Новая почта'
                        placeholder='name@example.com'
                        size='lg'
                        name='new-email'
                        className='fig-auth-field'
                        value={newEmail}
                        onChange={(val) => setNewEmail(val)}
                      />
                      <Button
                        type='button'
                        variant='filled'
                        shape='brick'
                        widthFullSize
                        loading={changeEmail.isPending}
                        disabled={changeEmail.isPending || !newEmail.includes('@')}
                        onClick={() =>
                          changeEmail.mutate(
                            {
                              email: blocked.email || values.email,
                              password: values.password,
                              new_email: newEmail.trim(),
                            },
                            {
                              onSuccess: (data) => {
                                if (data.email) {
                                  setFieldValue('email', data.email);
                                  setBlocked((prev) =>
                                    prev
                                      ? { ...prev, email: data.email, email_sent: true }
                                      : prev,
                                  );
                                }
                                setShowChange(false);
                                setNewEmail('');
                              },
                            },
                          )
                        }
                      >
                        Сохранить адрес и отправить письмо
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {values.method === 'phone' && phoneMode !== 'password' ? null : (
                <Button
                  type='submit'
                  variant='filled'
                  shape='brick'
                  loading={loginApi.isPending}
                  disabled={loginApi.isPending}
                  widthFullSize
                  className={classes.submitButton}
                >
                  Войти
                </Button>
              )}

              {/* Низ формы по макету 3:31561: «Забыли пароль?», разделитель, регистрация. */}
              <NavLink to='/forgot-password' className={classes.forgotLink}>
                Забыли пароль?
              </NavLink>
              {values.method === 'phone' && phoneMode === 'password' ? (
                <button
                  type='button'
                  className={classes.codeLink}
                  onClick={() => {
                    setPhoneMode('choice');
                    setFieldValue('password', '');
                  }}
                >
                  Войти по коду
                </button>
              ) : null}
              <div className={classes.divider} role='separator' />
              <div className={classes.registerBlock}>
                <span>Еще нет аккаунта?</span>
                <NavLink
                  to={withReturnToQuery('/registration', returnTo)}
                  className={classes.registerLink}
                >
                  Зарегистрироваться
                </NavLink>
              </div>
            </Form>
            );
          }}
        </Formik>
      </main>
    </section>
  );
};
